// ==========================
// Version 20 - pages/PublicMap.js
// - Logged-out visitors see a visitor-safe preview map
// - Signed-in users see real public BWK memory pins
// - Keeps media gated to authenticated access
// - Keeps mapId wiring and SEO
// ==========================
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { fetchPublicMemories } from '../db';
import { MAPS_LOADER_OPTIONS, withBrandMapOptions } from '../mapsConfig';
import { useAuth } from '../context/AuthContext';
import Seo from '../components/Seo';

const containerStyle = { width: '100%', height: '80vh', borderRadius: '12px' };
const defaultCenter = { lat: 20, lng: 0 };
const PREVIEW_PINS = [
  { id: 'preview-london', lat: 51.5074, lng: -0.1278 },
  { id: 'preview-paris', lat: 48.8566, lng: 2.3522 },
  { id: 'preview-new-york', lat: 40.7128, lng: -74.0060 },
  { id: 'preview-rio', lat: -22.9068, lng: -43.1729 },
  { id: 'preview-cape-town', lat: -33.9249, lng: 18.4241 },
  { id: 'preview-mumbai', lat: 19.0760, lng: 72.8777 },
  { id: 'preview-tokyo', lat: 35.6762, lng: 139.6503 },
  { id: 'preview-sydney', lat: -33.8688, lng: 151.2093 },
];

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const BURGUNDY = '#5C1A2B';
const TITLE_COLOR = NAVY;
const TEXT_COLOR = '#1b1b1b';
const META_COLOR = '#4a4a4a';

function pinUrl({ body = NAVY, ring = GOLD, highlight = '#ffffff', shadow = 'rgba(0,0,0,0.25)' } = {}) {
  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="44" height="56" viewBox="0 0 44 56">
    <ellipse cx="22" cy="52" rx="10" ry="4" fill="${shadow}"/>
    <path d="M22 2c10 0 18 7.8 18 17.4C40 33.4 22 54 22 54S4 33.4 4 19.4C4 9.8 12 2 22 2z" fill="${body}"/>
    <path d="M16 7c-5 3.5-8 9-8 12.9C8 31 22 47 22 47V5.5C19.5 5.8 17.6 6.4 16 7z" fill="${highlight}" opacity="0.10"/>
    <circle cx="22" cy="19" r="7.5" fill="none" stroke="${ring}" stroke-width="2.5"/>
    <circle cx="22" cy="19" r="4.2" fill="${ring}"/>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export default function PublicMap() {
  const { isLoaded, loadError } = useJsApiLoader(MAPS_LOADER_OPTIONS);
  const { currentUser } = useAuth();
  const isVisitorPreview = !currentUser;

  const [pins, setPins] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loadingPins, setLoadingPins] = useState(true);

  const mapRef = useRef(null);
  const didFitRef = useRef(false);
  const fitTimerRef = useRef(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!currentUser) {
        if (!alive) return;
        setPins(PREVIEW_PINS);
        setLoadingPins(false);
        return;
      }

      try {
        const docs = await fetchPublicMemories();
        if (!alive) return;

        const items = (docs || []).map((d) => {
          const lat = d?.lat ?? d?.location?.lat;
          const lng = d?.lng ?? d?.location?.lng;
          return {
            id: d.id,
            lat: Number(lat),
            lng: Number(lng),
            date: d.date ?? null,
            image: Array.isArray(d.imageUrls) && d.imageUrls.length ? d.imageUrls[0] : null,
            isMediaPublic: !!d.isMediaPublic,
          };
        });

        setPins(items);
      } finally {
        if (alive) setLoadingPins(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [currentUser]);

  const safePins = useMemo(
    () => pins.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)),
    [pins]
  );

  const fitAll = useCallback((map, allPins) => {
    if (!map || !window.google || !allPins.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    allPins.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds);
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (safePins.length && !didFitRef.current) {
      fitTimerRef.current = setTimeout(() => {
        fitAll(map, safePins);
        didFitRef.current = true;
      }, 120);
    } else {
      map.setCenter(defaultCenter);
    }
  }, [fitAll, safePins]);

  useEffect(() => {
    if (mapRef.current && safePins.length && !didFitRef.current) {
      if (fitTimerRef.current) clearTimeout(fitTimerRef.current);
      fitTimerRef.current = setTimeout(() => {
        fitAll(mapRef.current, safePins);
        didFitRef.current = true;
      }, 120);
    }
    return () => {
      if (fitTimerRef.current) clearTimeout(fitTimerRef.current);
    };
  }, [safePins, fitAll]);

  const icon = { url: pinUrl({}), scaledSize: { width: 44, height: 56 }, anchor: { x: 22, y: 54 } };
  const activePin = activeId ? safePins.find((p) => p.id === activeId) : null;
  const pageUrl = typeof window !== 'undefined' ? `${window.location.origin}/map` : undefined;
  const pageTitle = isVisitorPreview ? 'Community Map Preview' : 'Global Memories Map';
  const pageDescription = isVisitorPreview
    ? 'Preview the BWK world map experience. Sign in to explore real public memories shared inside the app.'
    : 'Explore public memory pins around the world inside the authenticated BWK experience.';

  if (loadError) {
    return (
      <div style={{ maxWidth: 1100, margin: '20px auto', padding: '0 12px', color: BURGUNDY }}>
        Failed to load Google Maps.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '20px auto', padding: '0 12px' }}>
      <Seo
        title={`${pageTitle} • Blessed with a Kiss`}
        description={pageDescription}
        url={pageUrl}
        type="website"
        noindex={false}
      />

      <h2 style={{ textAlign: 'center', marginBottom: 12, fontFamily: 'serif' }}>
        {pageTitle}
      </h2>

      {isVisitorPreview ? (
        <div style={noticeCard}>
          <div style={noticeTitle}>Visitor Preview</div>
          <p style={noticeText}>
            This map is a preview of the BWK experience. Sign in to explore real public memories and media shared inside the app.
          </p>
          <a href="/login" style={noticeBtn}>Sign in to explore</a>
        </div>
      ) : null}

      {!isLoaded ? (
        <div style={skeletonBox} aria-busy="true" aria-live="polite">
          Loading map...
        </div>
      ) : safePins.length === 0 && !loadingPins ? (
        <div style={emptyWrap}>
          <div style={emptyCard}>
            <div style={emptyEmoji} aria-hidden>o_O</div>
            <div style={emptyTitle}>No public memories yet</div>
            <p style={emptyText}>
              Be the first to leave a pin on the world map. Create a memory from the Home page and mark it public.
            </p>
            <a href="/" style={emptyBtn}>Create a Memory</a>
          </div>
        </div>
      ) : (
        <GoogleMap
          mapContainerStyle={containerStyle}
          onLoad={onMapLoad}
          options={withBrandMapOptions()}
        >
          {safePins.map((p) => (
            <Marker
              key={p.id}
              position={{ lat: p.lat, lng: p.lng }}
              onClick={() => setActiveId(p.id)}
              icon={icon}
            />
          ))}

          {activePin && (
            <InfoWindow
              position={{ lat: activePin.lat, lng: activePin.lng }}
              onCloseClick={() => setActiveId(null)}
            >
              <div style={{ maxWidth: 240, color: TEXT_COLOR, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Inter, sans-serif' }}>
                {!isVisitorPreview && currentUser && activePin.isMediaPublic && activePin.image ? (
                  <img
                    src={activePin.image}
                    alt=""
                    width="240"
                    height="120"
                    decoding="async"
                    loading="lazy"
                    style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 8 }}
                  />
                ) : null}

                {isVisitorPreview ? (
                  <>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: TITLE_COLOR }}>
                      BWK Preview Pin
                    </div>
                    <div style={{ fontSize: 13, color: META_COLOR, marginBottom: 8 }}>
                      Sign in to explore real public memories inside BWK.
                    </div>
                    <a
                      href="/login"
                      style={{
                        display: 'inline-block',
                        textDecoration: 'none',
                        fontWeight: 800,
                        padding: '6px 10px',
                        borderRadius: 10,
                        background: GOLD,
                        color: NAVY,
                        border: `1px solid ${GOLD}`,
                      }}
                    >
                      Sign in ->
                    </a>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: TITLE_COLOR }}>
                      Memory
                    </div>
                    <div style={{ fontSize: 13, color: META_COLOR, marginBottom: 8 }}>
                      Date: {activePin.date ? String(activePin.date) : 'No date'}
                    </div>

                    <a
                      href={`/m/${activePin.id}`}
                      style={{
                        display: 'inline-block',
                        textDecoration: 'none',
                        fontWeight: 800,
                        padding: '6px 10px',
                        borderRadius: 10,
                        background: GOLD,
                        color: NAVY,
                        border: `1px solid ${GOLD}`,
                      }}
                    >
                      Open memory ->
                    </a>
                  </>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}

      {loadingPins && (
        <p style={{ marginTop: 8, textAlign: 'center' }}>
          {isVisitorPreview ? 'Loading preview pins...' : 'Loading public pins...'}
        </p>
      )}
      {!loadingPins && safePins.length > 0 && (
        <p style={{ marginTop: 8, textAlign: 'center' }}>
          {isVisitorPreview
            ? 'Visitors are seeing a preview map. Sign in to explore real public memories.'
            : 'Pins are anonymous. Signed-in users can view public memories and media when owners allowed it.'}
        </p>
      )}
    </div>
  );
}

const skeletonBox = {
  width: '100%',
  height: '60vh',
  borderRadius: 12,
  border: '1px solid rgba(201,164,76,0.20)',
  background: 'linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.12), rgba(255,255,255,0.06))',
  backgroundSize: '200% 100%',
  animation: 'bwak-shimmer 1.2s linear infinite',
  display: 'grid',
  placeItems: 'center',
  color: '#ddd',
};

const emptyWrap = { display: 'grid', placeItems: 'center' };
const emptyCard = {
  width: 'min(92vw, 520px)',
  background: '#FDFCF9',
  border: '1px solid rgba(201,164,76,0.35)',
  borderRadius: 16,
  padding: 20,
  textAlign: 'center',
  boxShadow: '0 12px 36px rgba(10,26,51,0.28)',
  color: '#0A1A33',
};
const emptyEmoji = {
  width: 56,
  height: 56,
  borderRadius: '50%',
  border: '2px solid #C9A44C',
  display: 'grid',
  placeItems: 'center',
  margin: '0 auto 8px',
  fontSize: 28,
};
const emptyTitle = { fontFamily: 'serif', fontSize: 22, fontWeight: 700, marginBottom: 6 };
const emptyText = { color: '#888888', marginTop: 0, marginBottom: 12 };
const emptyBtn = {
  display: 'inline-block',
  padding: '8px 12px',
  borderRadius: 10,
  background: '#C9A44C',
  color: '#0A1A33',
  fontWeight: 900,
  textDecoration: 'none',
};
const noticeCard = {
  maxWidth: 720,
  margin: '0 auto 16px',
  background: '#FDFCF9',
  border: '1px solid rgba(201,164,76,0.35)',
  borderRadius: 16,
  padding: 18,
  textAlign: 'center',
  boxShadow: '0 10px 30px rgba(10,26,51,0.14)',
  color: NAVY,
};
const noticeTitle = { fontFamily: 'serif', fontSize: 24, fontWeight: 700, marginBottom: 6 };
const noticeText = { color: META_COLOR, margin: '0 0 12px' };
const noticeBtn = {
  display: 'inline-block',
  padding: '8px 12px',
  borderRadius: 10,
  background: GOLD,
  color: NAVY,
  fontWeight: 900,
  textDecoration: 'none',
};

if (typeof document !== 'undefined' && !document.getElementById('bwak-shimmer-kf')) {
  const s = document.createElement('style');
  s.id = 'bwak-shimmer-kf';
  s.textContent = '@keyframes bwak-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }';
  document.head.appendChild(s);
}
// ==========================
// End Version 20 - pages/PublicMap.js
// ==========================
